import React, { useCallback, useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import Reveal from './Reveal';
import { useWallet } from '../wallet/WalletContext';
import { fetchEligibleNfts, fetchClaimable, fetchRewardTable, formatKek } from '../wallet/chain';
import { sendNftsForRedemption, claimRewards } from '../wallet/actions';
import { CONTRACT_ACCOUNT, COLLECTION, REWARD_SYMBOL_CODE } from '../config/redeem';
import { ArrowIcon, CheckIcon, FireIcon } from './icons';
import '../styles/redeem.css';

const Redeem = () => {
  const { session, actor, restoring, login, logout } = useWallet();

  const [nfts, setNfts] = useState([]);
  const [selected, setSelected] = useState(() => new Set());
  const [rewardMap, setRewardMap] = useState({});
  const [claimable, setClaimable] = useState(0);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState(null);

  const flash = (type, text) => {
    setStatus({ type, text });
    setTimeout(() => setStatus(null), 6000);
  };

  const loadData = useCallback(async () => {
    if (!actor) return;
    setLoading(true);
    try {
      const [nftList, bal, rewards] = await Promise.all([
        fetchEligibleNfts(actor),
        fetchClaimable(actor),
        fetchRewardTable(),
      ]);
      setNfts(nftList);
      setClaimable(bal);
      setRewardMap(rewards);
      setSelected(new Set());
    } catch (e) {
      flash('error', e.message || 'Failed to load on-chain data');
    } finally {
      setLoading(false);
    }
  }, [actor]);

  useEffect(() => { if (actor) loadData(); }, [actor, loadData]);

  const toggle = (assetId) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(assetId) ? next.delete(assetId) : next.add(assetId);
      return next;
    });
  };
  const selectAll = () => setSelected(new Set(nfts.map((n) => n.assetId)));
  const clearSel = () => setSelected(new Set());

  const estReward = nfts
    .filter((n) => selected.has(n.assetId))
    .reduce((sum, n) => sum + (rewardMap[n.templateId] || 0), 0);

  const handleConnect = async () => {
    try { await login(); } catch (e) { if (e?.message) flash('error', e.message); }
  };

  const handleSend = async () => {
    if (!session || selected.size === 0) return;
    setBusy(true);
    try {
      await sendNftsForRedemption(session, [...selected]);
      flash('success', `Sent ${selected.size} NFT${selected.size > 1 ? 's' : ''} — rewards credited. Claim below!`);
      await loadData();
    } catch (e) {
      flash('error', e.message || 'Transaction failed');
    } finally {
      setBusy(false);
    }
  };

  const handleClaim = async () => {
    if (!session || claimable <= 0) return;
    setBusy(true);
    try {
      await claimRewards(session);
      flash('success', 'Rewards claimed! 🐸');
      await loadData();
    } catch (e) {
      flash('error', e.message || 'Claim failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="section redeem" id="redeem">
      <Reveal className="redeem-head">
        <span className="eyebrow">nft rewards</span>
        <h2 className="section-title">redeem your <span className="accent">sKEK NFTs</span></h2>
        <p className="section-lead">
          Send eligible <code>{COLLECTION}</code> NFTs to the vault, bank your $KEK rewards,
          and claim them straight to your wallet — all on-chain.
        </p>
      </Reveal>

      {!actor ? (
        <Reveal className="redeem-connect glass">
          <div className="redeem-connect-icon"><FireIcon /></div>
          <h3>Connect your WAX wallet</h3>
          <p>Anchor, Cloud Wallet or Wombat — connect to see your eligible NFTs and rewards.</p>
          <button className="btn btn-primary" onClick={handleConnect} disabled={restoring}>
            {restoring ? 'Checking…' : 'Connect wallet'}
          </button>
        </Reveal>
      ) : (
        <div className="redeem-grid">
          {/* NFTs panel */}
          <Reveal className="redeem-panel glass">
            <div className="redeem-panel-head">
              <h3>Your eligible NFTs</h3>
              <div className="redeem-panel-tools">
                <span className="chip">{actor}</span>
                <button className="redeem-mini" onClick={loadData} disabled={loading || busy}>refresh</button>
              </div>
            </div>

            <div className="redeem-warn">
              <FireIcon />
              <span>Redemption is permanent — NFTs you send are <strong>burned</strong> in exchange for $KEK.</span>
            </div>

            {loading ? (
              <div className="redeem-empty">Loading your NFTs…</div>
            ) : nfts.length === 0 ? (
              <div className="redeem-empty">No eligible NFTs found in this wallet.</div>
            ) : (
              <>
                <div className="redeem-selectbar">
                  <button className="redeem-mini" onClick={selectAll} disabled={busy}>select all</button>
                  <button className="redeem-mini" onClick={clearSel} disabled={busy}>clear</button>
                  <span className="redeem-selcount">{selected.size} selected</span>
                </div>

                <div className="redeem-nfts">
                  {nfts.map((n) => {
                    const isSel = selected.has(n.assetId);
                    return (
                      <button
                        key={n.assetId}
                        className={`nft-card ${isSel ? 'is-selected' : ''}`}
                        onClick={() => toggle(n.assetId)}
                        disabled={busy}
                      >
                        <span className="nft-check">{isSel && <CheckIcon />}</span>
                        <span className="nft-img">
                          {n.img
                            ? <img src={n.img} alt={n.name} loading="lazy" />
                            : <span className="nft-noimg">🐸</span>}
                        </span>
                        <span className="nft-meta">
                          <span className="nft-name">{n.name}</span>
                          <span className="nft-reward">
                            {rewardMap[n.templateId] != null
                              ? formatKek(rewardMap[n.templateId])
                              : `— ${REWARD_SYMBOL_CODE}`}
                          </span>
                        </span>
                      </button>
                    );
                  })}
                </div>

                <div className="redeem-send">
                  <div className="redeem-send-est">
                    <span>Est. reward</span>
                    <strong>{formatKek(estReward)}</strong>
                  </div>
                  <button
                    className="btn btn-primary"
                    onClick={handleSend}
                    disabled={busy || selected.size === 0}
                  >
                    {busy ? 'Confirm in wallet…' : <>Redeem {selected.size || ''} NFT{selected.size === 1 ? '' : 's'} <ArrowIcon /></>}
                  </button>
                </div>
              </>
            )}
          </Reveal>

          {/* Rewards panel */}
          <Reveal className="redeem-panel redeem-rewards glass" delay={0.1}>
            <h3>Claimable rewards</h3>
            <div className="redeem-balance">
              <span className="redeem-balance-value">{formatKek(claimable)}</span>
              <span className="redeem-balance-label">pending in the vault</span>
            </div>
            <button
              className="btn btn-primary redeem-claim"
              onClick={handleClaim}
              disabled={busy || claimable <= 0}
            >
              {busy ? 'Confirm in wallet…' : 'Claim rewards'}
            </button>
            <p className="redeem-note">
              Rewards accrue when you redeem NFTs and stay claimable until you withdraw them.
            </p>
            <div className="redeem-foot">
              <span>vault · <code>{CONTRACT_ACCOUNT}</code></span>
              <button className="redeem-mini" onClick={logout} disabled={busy}>disconnect</button>
            </div>
          </Reveal>
        </div>
      )}

      <AnimatePresence>
        {status && (
          <motion.div
            className={`redeem-toast redeem-toast--${status.type}`}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
          >
            {status.text}
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default Redeem;
